/**
 * @file TabelaGeral.js
 * @description Componente da tabela de classificação geral com expand/collapse.
 */
import { useState, useMemo } from "react";
import { ChevronDown, Star } from "lucide-react";
import { getEscudoUrl } from "../api/ligaApi";
import EscalacaoModal from "./EscalacaoModal";


const TabelaClassificacaoGeral = ({ equipes, rodadasData, parciais }) => {
  // Permite várias equipes expandidas ao mesmo tempo
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Time selecionado para ver a escalação no Cartola
  const [escalacaoTime, setEscalacaoTime] = useState(null);

  const handleToggleExpand = (equipeId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(equipeId)) {
        next.delete(equipeId);
      } else {
        next.add(equipeId);
      }
      return next;
    });
  };

  // Recalcula o ranking só quando os dados realmente mudam
  const leaderboard = useMemo(() => {
    // Parcial da rodada ao vivo (quando existir) entra na soma de cada time
    const getParcialTime = (timeId) =>
      parciais ? parciais.valores[timeId.toString()] ?? 0 : 0;

    const calcularPontuacaoTotalTime = (timeId) => {
      const acumulado = rodadasData
        .filter((p) => p.time_id && p.time_id.toString() === timeId.toString())
        .reduce((acc, curr) => acc + curr.pontos, 0);
      return acumulado + getParcialTime(timeId);
    };

    return equipes
      .map((equipe) => {
        const times = equipe.times
          .map((time) => ({
            ...time,
            pontuacaoGeral: calcularPontuacaoTotalTime(time.id),
          }))
          .sort((a, b) => b.pontuacaoGeral - a.pontuacaoGeral);
        return {
          ...equipe,
          times,
          pontuacaoGeral: times.reduce((acc, t) => acc + t.pontuacaoGeral, 0),
        };
      })
      .sort((a, b) => b.pontuacaoGeral - a.pontuacaoGeral);
  }, [equipes, rodadasData, parciais]);

  if (leaderboard.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500 text-sm">Nenhuma equipe cadastrada.</p>
      </div>
    );
  }

  // Sem rodadas concluídas nem parciais ao vivo todos estão zerados:
  // não há ranking a exibir
  const temRodadasConcluidas =
    rodadasData.some((p) => p.pontos != null) || !!parciais;

  return (
    <div className="space-y-2">
      {leaderboard.map((equipe, index) => {
        const isExpanded = expandedIds.has(equipe.id);

        // Estilo de pódio (ouro, prata e bronze) só quando já há pontuação
        const podiumCard =
          !temRodadasConcluidas
            ? "border-campo-700/50 bg-campo-950/50"
            : index === 0
            ? "border-ouro/40 bg-gradient-to-r from-ouro/10 via-campo-950/50 to-campo-950/50"
            : index === 1
            ? "border-gray-400/30 bg-campo-950/50"
            : index === 2
            ? "border-amber-700/40 bg-campo-950/50"
            : "border-campo-700/50 bg-campo-950/50";

        const podiumPos =
          !temRodadasConcluidas
            ? "text-campo-600"
            : index === 0
            ? "text-ouro"
            : index === 1
            ? "text-gray-300"
            : index === 2
            ? "text-amber-600"
            : "text-gray-500";

        return (
          <div
            key={equipe.id}
            className={`rounded-xl border overflow-hidden ${podiumCard}`}
          >
            {/* Linha principal clicável */}
            <div
              role="button"
              aria-expanded={isExpanded}
              className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 cursor-pointer select-none hover:bg-white/5 active:bg-white/10 transition-colors"
              onClick={() => handleToggleExpand(equipe.id)}
            >
              {/* Posição */}
              <div
                className={`w-7 sm:w-8 flex items-center justify-center font-black text-base sm:text-lg flex-shrink-0 ${podiumPos}`}
              >
                {temRodadasConcluidas ? index + 1 : "–"}
              </div>

              {/* Logo */}
              <img
                src={getEscudoUrl(equipe.logo_url) || "/logo-equipe-padrao.png"}
                alt=""
                loading="lazy"
                className="w-9 h-9 rounded-full object-contain flex-shrink-0"
              />

              {/* Nome + Títulos */}
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <span className="font-bold text-white text-sm sm:text-base truncate">
                  {equipe.nome}
                </span>
                {equipe.titulos > 0 && (
                  <div
                    className="relative flex items-center justify-center w-5 h-5 flex-shrink-0"
                    title={`${equipe.titulos} Títulos`}
                  >
                    <Star size={20} className="text-ouro fill-ouro" />
                    <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-campo-950">
                      {equipe.titulos}
                    </span>
                  </div>
                )}
              </div>

              {/* Pontuação + ícone de expand */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="font-black text-ouro text-base sm:text-lg tabular-nums">
                  {equipe.pontuacaoGeral.toFixed(2)}
                </span>
                <span className="text-gray-500 text-xs hidden sm:inline ml-0.5">pts</span>
                <ChevronDown
                  size={15}
                  className={`text-gray-400 ml-1.5 transition-transform duration-200 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
              </div>
            </div>

            {/* Detalhes expandidos */}
            {isExpanded && (
              <div className="border-t border-white/10 px-3 sm:px-4 pb-3">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider pt-3 pb-2">
                  Pontuação Acumulada por Time
                </p>
                <div className="space-y-1.5">
                  {equipe.times.map((time, tIdx) => (
                    <div
                      key={time.id}
                      role="button"
                      title="Ver escalação no Cartola"
                      onClick={() => setEscalacaoTime(time)}
                      className="flex items-center gap-2 bg-campo-900/70 p-2 sm:p-2.5 rounded-lg cursor-pointer select-none hover:bg-campo-800 active:bg-campo-700/70 transition-colors"
                    >
                      <span className="text-xs text-gray-600 w-4 text-center flex-shrink-0">
                        {tIdx + 1}
                      </span>
                      <img
                        src={getEscudoUrl(time.escudo_url)}
                        alt={`Escudo do ${time.nome_time}`}
                        loading="lazy"
                        className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-white truncate flex items-center gap-1.5">
                          <span className="truncate">{time.nome_time}</span>
                          {time.is_captain && (
                            <span className="text-ouro font-bold text-xs flex-shrink-0">
                              (C)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{time.nome_cartoleiro}</p>
                      </div>
                      <span className="font-bold text-ouro text-sm tabular-nums flex-shrink-0">
                        {time.pontuacaoGeral.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {escalacaoTime && (
        <EscalacaoModal
          time={escalacaoTime}
          onClose={() => setEscalacaoTime(null)}
        />
      )}
    </div>
  );
};

export default TabelaClassificacaoGeral;
